import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptionsFromEnumAndPrefix } from 'components/final-form/radioUtils';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import LogoDark from 'LogoDark';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Grid, Icon, RadioProps } from 'semantic-ui-react';
import {
  getCovidTestRegistrationData,
  getPublicOrderItemResult,
  registerTestPanelTestkitResult
} from 'services/testkitServices';
import styled from 'styled-components';
import { InterventionBookingTestResult, LabTestResultType } from 'ts-types/api.enums';
import {
  CovidTestRegistrationData,
  InterventionOrderItemResultDto,
  LabTestComponentResultDto,
  TestPanelTestkitRegistrationRequestDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { composeValidators, ctValidator, mocValidator, required } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';


const StyledLogo = styled(LogoDark)`
  &.logo-icon {
    transform: scale(0.6);
    margin-top: 0.2rem;
    margin-left: -2.5rem;
    height: 2rem;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0 1.5rem;

  .page-header {
    flex: 0 0 auto;
    border-bottom: 1px solid #2D404A;
    padding: 0.9rem 0;
    font-size: 1.4rem;
    margin: 0 0 2rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .praxmedic-logo-container {
      flex: 1 1 auto;
      width: 2.675em;
      max-width: 9.675rem;
      height: 2.675rem;
    }

    .header-title {
      flex: 0 0 auto;
      line-height: 2.775rem;
      color: #0f1a52;
    }
  }

  .page-footer {
    flex: 0 0 auto;
    border-top: 1px solid #2D404A;
    padding: 0.35rem 1rem 0.5rem;
    font-size: 1.1rem;
    margin: 1.2rem 0 0;
    color: #0f1a52;

    .left {
      float: left;
    }

    .right {
      float: right;
    }
  }

  .result-form .error {
    margin: 0;
    padding: 0;
    background: none;
  }

  @media only screen and (min-width: 320px) and (max-width: 767px) {
    .page-header {
      margin-bottom: 0.3rem;
    }

    .page-footer {
      padding: 0.35rem 0.15rem 0.5rem;
      font-size: 1rem;
    }
  }
`;

const StyledContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: start;
  flex-direction: column;
  max-width: 768px;

  .info-text {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  video {
    border: 2px solid;
  }

  .snapshot-container {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: center;

    .image-input {
      display: none;
    }

    .snapshot-placeholder {
      display: flex;
      flex-direction: column;
      max-width: 80vh;
      min-width: 30vh;
      min-height: 20vh;
      align-items: center;
      border: 1px solid #e5e5e5;
      padding-top: 1rem;
      border-radius: .28571429rem;

      i.icon {
        color: #e5e5e5;
      }
    }

    .take-snapshot-action {
      display: flex;
      flex-direction: column;
      margin-top: 1rem;
      align-items: center;

      i.icon {
        color: #2185d0;
      }

      .action-label {
        color: #1678c2;
        font-size: 1.1rem;
      }
    }

    .image-box {
      max-width: 90%;
      max-height: 50vh;
      margin: 10px;
      text-align: center;

      img {
        height: inherit;
        max-height: inherit;
        max-width: inherit;
      }
    }
  }

  .warning, .success, .errorMessage {
    border-radius: .28571429rem;
    padding: 1rem;
    font-size: 1.2rem;
    margin-bottom: .5rem;
    width: 100%;
  }

  .warning {
    box-shadow: 0 0 0 1px #c9ba9b inset, 0 0 0 0 transparent;
    background-color: #fffaf3;
    color: #573a08;
  }

  .success {
    box-shadow: 0 0 0 1px #a3c293 inset, 0 0 0 0 transparent;
    background-color: #fcfff5;
    color: #2c662d;
  }

  .errorMessage {
    box-shadow: 0 0 0 1px #e0b4b4 inset, 0 0 0 0 transparent;
    background-color: #fff6f6;
    color: #9f3a38;
  }

  .info-message {
    margin: 1rem 0;
    font-size: 1.2rem;
  }
`;

const StyledInfoRow = styled.div`
  background-color: ${(props: InfoRowProps) => props.backgroundColor || 'inherited'};
  width: 100%;
  display: flex;
  padding: 0.5rem;
  border-radius: .28571429rem;

  *:first-child {
    width: 150px;
  }

  *:nth-child(2) {
    flex: 1;
  }

  &.target-employee {
    margin-bottom: 1.5rem;
  }
`;

const StyledForm = styled.form`
  padding: 0.75rem;
  width: 100%;

  .field-label {
    margin-top: 0.4rem;
    margin-bottom: 0.7rem;
  }

  .field-label:not(:first-child) {
  }

  .radio-group .ui.radio.checkbox {
    margin-right: 1.5rem;
  }

  & .ui.grid {
    padding-left: 0 !important;
  }

  .field-error {
    color: darkred;
    font-size: 1rem;
  }

  .loader-box {
    position: relative;
    width: 300px;
    height: 90px;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .ui.button {
    margin-right: 0.75rem;
  }
`;

interface InfoRowProps {
  backgroundColor?: string;
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps {
}

type PageState = "ENTRY" | "SUCCESS" | "ERROR" | "FILE_SIZE_EXCEEDED" | "INVALID_TOKEN";

let initialValues: Partial<InterventionOrderItemResultDto> = {};

const TestPanelResultImageUpload = (props: Props) => {

  const {getMessage, currentUser, match} = props;

  const [cancelTokenSource] = React.useState(axios.CancelToken.source());
  const [pageState, setPageState] = React.useState("ENTRY" as PageState);
  const [officialCertificateWarning, setOfficialCertificateWarning] = React.useState(false);
  const [testData, setTestData] = useState({} as CovidTestRegistrationData);
  const [componentRadioPropsMap, setComponentRadioPropsMap] = useState({} as { [id: string]: RadioProps[] })
  const [orderItemResult, setOrderItemResult] = useState({} as InterventionOrderItemResultDto);
  const [dataLoading, setDataLoading] = useState(true);
  const [testResultSnapshot, setTestResultSnapshot] = useState(undefined);
  const [source, setSource] = useState("");
  const [ctValue, setCtValue] = useState(undefined as string | undefined);
  const [ctError, setCtError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);

      try {
        //@ts-ignore
        const token: string = match.params.token;

        const result = await getCovidTestRegistrationData(token, cancelTokenSource)
        setTestData(result);

        //@ts-ignore
        const orderItemResult = await getPublicOrderItemResult(token, cancelTokenSource);
        setOrderItemResult(orderItemResult);

        if (orderItemResult) {
          initialValues = {
            components: orderItemResult.components
          };

          const orderItemComponents = orderItemResult.components;
          const componentRadioPropsMap: { [id: string]: RadioProps[] } = {};
          orderItemComponents.forEach(orderItemPanel => {

            const components: LabTestComponentResultDto[] = orderItemPanel.components;

            components.forEach(component => {
              if (LabTestResultType.POSITIVE_NEGATIVE_CT === component.resultType) {

                const testResults = [
                  InterventionBookingTestResult.POSITIVE,
                  InterventionBookingTestResult.NEGATIVE
                ];

                componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                    testResults, component.code, "enum.testResult", props.getMessage);

              } else if (LabTestResultType.POSITIVE_NEGATIVE_UNCERTAIN_CT === component.resultType) {

                const testResults = [
                  InterventionBookingTestResult.POSITIVE,
                  InterventionBookingTestResult.NEGATIVE,
                  InterventionBookingTestResult.UNCERTAIN];

                componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                    testResults, component.code, "enum.testResult", props.getMessage);
              } else if (LabTestResultType.POSITIVE_NEGATIVE_BORDERLINE_MOC === component.resultType) {

                const testResults = [
                  InterventionBookingTestResult.POSITIVE,
                  InterventionBookingTestResult.NEGATIVE,
                  InterventionBookingTestResult.BORDERLINE];

                componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                    testResults, component.code, "enum.testResult", props.getMessage);
              }
            });
          });

          setComponentRadioPropsMap(componentRadioPropsMap);
        }
        setDataLoading(false);

      } catch (e) {
        handleError(e.response.data);
      } finally {
        setDataLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleError = (error: any) => {
    const knownErrors: Array<string> = [
      errorUtils.invalidToken,
      errorUtils.unknownEmployee
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode) && violation.errorCode === errorUtils.invalidToken) {
          setPageState("INVALID_TOKEN");
          setErrorMessage(getMessage(`error.${errorUtils.invalidToken}`));
        } else {
          setPageState("ERROR");
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      setPageState("ERROR");
      setErrorMessage(getMessage("error.generalErrorMessage"))
    }
  };

  const handleCapture = (target: any) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const file = target.files[0];

        if (file.size <= 10485760) {
          setTestResultSnapshot(file);
          const newUrl = URL.createObjectURL(file);
          setSource(newUrl);
        } else {
          setPageState("FILE_SIZE_EXCEEDED");
        }
      }
    }
  };

  const parseCtMocInput = (value: any) => {
    if (!value || value.length < 6) {
      return value;
    } else {
      return value.slice(0, 5);
    }
  };


  const handleSubmit = async (values: any) => {

    try {

      //@ts-ignore
      const token: string = match.params.token.toString();

      const requestDto: TestPanelTestkitRegistrationRequestDto = {
        token,
        components: values.components!
      };

      const responseDto = await registerTestPanelTestkitResult(
          requestDto,
          testResultSnapshot,
          cancelTokenSource);

      if (responseDto.certificateError) {

        setOfficialCertificateWarning(true);
      }
      setPageState("SUCCESS");
    } catch (e) {
      if (e.message === "Network Error") {
        setPageState("FILE_SIZE_EXCEEDED");
      } else {
        handleError(e.response.data);
      }
    }

  }

  const takeSnapshotKey = source
      ? "covidTestkitSnapshot.action.retakeSnapshot"
      : "covidTestkitSnapshot.action.takeSnapshot";

  const renderTopPageData = () => <>
    <StyledInfoRow>
      <span>{getMessage("covidTestkitSnapshot.content.administrator")}</span>
      <span><b>{`${testData.userName} ${testData.userFirstName}`}</b></span>
    </StyledInfoRow>

    <h3>{getMessage("covidTestkitSnapshot.action.takeSnapshot")}</h3>

    <StyledInfoRow className="target-employee" backgroundColor="#e5e5e5">
      <span>{getMessage("covidTestkitSnapshot.content.employee")}</span>
      <span><b>{`${testData.patientName} ${testData.patientFirstName},
      ${moment(testData.patientBirthDate).format("DD.MM.YYYY")}`}</b></span>
    </StyledInfoRow>
  </>;

  const renderInputTestResultForm = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            subscription={{pristine: true, submitting: true, values: true, errors: true}}
            render={renderForm}
        />
    );
  };

  const renderForm = ({form, values, handleSubmit, submitting, errors}: FormRenderProps): React.ReactNode => {

    const orderItemResult = values as InterventionOrderItemResultDto;

    let mappedParamRows: any = [];
    orderItemResult.components.forEach((
        orderItemPanel, orderItemComponentIx) => {

      const components: LabTestComponentResultDto[] = orderItemPanel.components;
      let showHeader = !orderItemPanel.simplePanel && components.length > 1;

      // @ts-ignore
      const mappedParamRow = components.map((
          component, componentIx) => {

        const isQuantityDecimal = component.resultType === LabTestResultType.QUANTITY_DECIMAL;
        const propertyPrefix = `components[${orderItemComponentIx}].components[${componentIx}]`;

        return (
            <React.Fragment key={component.code}>
              {
                  showHeader && componentIx === 0 &&
                  <div className="test-panel-header">
                    <b>{getMessage(`patientCentral.testResult.labTestPanel.${orderItemPanel.code}.name`)}</b>
                  </div>
              }

              <Grid.Row key={componentIx}
                        className={isQuantityDecimal ? "row-result" : ""}
              >
                <Grid.Column width={isQuantityDecimal ? 5 : 6} className="radio-group" verticalAlign="middle">
                  <div className="field-label">
                    {getMessage(`patientCentral.testResult.${component.code}`)}:
                  </div>
                  {
                      component.resultType !== LabTestResultType.QUANTITY_DECIMAL &&
                      <Field
                          name={`${propertyPrefix}.detectedResult`}
                          fluid
                          radiowidth="auto"
                          component={RadioGroup}
                          radioDefinitions={componentRadioPropsMap[component.code]}
                          validate={required}
                      />
                  }
                </Grid.Column>

                {
                    component.detectedResult === 'POSITIVE' && component.resultType.endsWith("_CT") &&
                    <Grid.Column width={3} verticalAlign="middle">
                      <div className="field-label">
                        {getMessage("patientCentral.testResult.ct")}:
                      </div>

                      <Field
                          fluid
                          name={`${propertyPrefix}.ctValue`}
                          component={Input}
                          placeholder="##.##"
                          style={{width: "4rem"}}
                          validate={ctValidator}
                          parse={parseCtMocInput}
                      />
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.ctValue`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.ctError`)}
                          </div>
                      }
                    </Grid.Column>
                }

                {
                    component.resultType === LabTestResultType.POSITIVE_NEGATIVE_BORDERLINE_MOC &&
                    <Grid.Column width={3} verticalAlign="middle">
                      <div className="field-label">
                        {getMessage("patientCentral.testResult.moc")}:
                      </div>

                      <Field
                          fluid
                          name={`${propertyPrefix}.mocValue`}
                          component={Input}
                          placeholder="##.##"
                          style={{width: "4rem"}}
                          validate={composeValidators(required, mocValidator)}
                          parse={parseCtMocInput}
                      />
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.mocValue`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.mocError`)}
                          </div>
                      }
                    </Grid.Column>
                }

                {
                    component.resultType === LabTestResultType.QUANTITY_DECIMAL &&
                    <Grid.Column width={4} verticalAlign="middle">
                      <div style={{display: "inline-flex", alignItems: "baseline"}}>
                        <Field
                            fluid
                            name={`${propertyPrefix}.quantity`}
                            component={Input}
                            style={{width: "4rem"}}
                            validate={required}
                        />
                        <span style={{paddingLeft: "10px"}}>
                          {component.exampleUnits}
                        </span>
                      </div>
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.quantity`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.quantityError`)}
                          </div>
                      }
                    </Grid.Column>
                }
              </Grid.Row>
            </React.Fragment>
        )
      });

      mappedParamRows.push(mappedParamRow);
    });

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContainer>
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column>
                  <div className="snapshot-container">
                    <input
                        accept="image/*"
                        className="image-input"
                        id="icon-button-file"
                        type="file"
                        capture="environment"
                        onChange={(e) => handleCapture(e.target)}
                    />
                    <label htmlFor="icon-button-file">
                      {
                        source
                            ?
                            <div className="image-box">
                              <img src={source} alt={"snap"} className=""/>
                            </div>
                            :
                            <div className="snapshot-placeholder">
                              <Icon.Group size="huge">
                                <Icon name="flask"/>
                                <Icon corner="top right" name="address card outline"/>
                              </Icon.Group>
                            </div>
                      }
                    </label>
                    <label htmlFor="icon-button-file">
                      <div className="take-snapshot-action">
                        <Icon name="camera" size="large"/>
                        <div className="action-label">
                          {getMessage(takeSnapshotKey)}
                        </div>
                      </div>
                    </label>
                  </div>
                </Grid.Column>
              </Grid.Row>
              {mappedParamRows}
            </PraxFormGrid>
            <FormButtonsRow>
              <Button
                  className="action-button"
                  primary
                  compact
                  disabled={form.getState().invalid || submitting || !orderItemResult || !testResultSnapshot}
              >
                {getMessage('covidTestkitSnapshot.action.register')}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </StyledForm>
    );
  };

  const renderEntryState = () => <>
    {renderTopPageData()}
    {renderInputTestResultForm()}
  </>;

  const renderSuccess = () => <>
    <div className="success">
      {getMessage("covidTestkitSnapshot.message.success")}
    </div>
    {
        officialCertificateWarning &&
        <div className="warning">
          {getMessage("covidTestkitSnapshot.message.warn.officialCertificateNotGenerated")}
        </div>
    }
    {renderTopPageData()}
  </>;

  const renderError = () => <>
    <div className="errorMessage">
      {errorMessage}
    </div>
    {pageState !== "INVALID_TOKEN" && renderTopPageData()}

    <div className="info-message">
      {getMessage("covidTestkitSnapshot.message.somethingWentWrong")}
    </div>

    {pageState !== "INVALID_TOKEN" &&
        <StyledButtonContainer>
          <Button
              type="button"
              className="action-button"
              color="grey"
              onClick={() => setPageState("ENTRY")}
          >
            {getMessage('covidTestkitSnapshot.action.registerAgain')}
          </Button>
        </StyledButtonContainer>
    }
  </>;

  const renderFileSizeExceeded = () => <>
    <div className="error">
      {getMessage("covidTestkitSnapshot.message.fileSizeExceeded")}
    </div>
    {renderTopPageData()}

    <div className="info-message">
      {getMessage("covidTestkitSnapshot.message.smallerImage")}
    </div>

    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          color="grey"
          onClick={() => setPageState("ENTRY")}
      >
        {getMessage('covidTestkitSnapshot.action.registerAgain')}
      </Button>
    </StyledButtonContainer>
  </>;

  return <MainContainer>
    <div className="page-header">
      <div className="praxmedic-logo-container">
        <StyledLogo/>
      </div>
    </div>
    <StyledContainer>
      {dataLoading
          ? <StyledViewTableRow>
            <LoaderComponent active/>
          </StyledViewTableRow>
          : <>
            {
                pageState === "ENTRY" &&
                renderEntryState()
            }
            {
                pageState === "SUCCESS" &&
                renderSuccess()
            }
            {
                pageState === "ERROR" &&
                renderError()
            }
            {
                pageState === "INVALID_TOKEN" &&
                renderError()
            }
            {
                pageState === "FILE_SIZE_EXCEEDED" &&
                renderFileSizeExceeded()
            }
          </>
      }
    </StyledContainer>
    <div className="page-footer">
      <div className="left">{getMessage("page.footer.copyright")}</div>
      <div className="right">{getMessage("page.footer.appName")}</div>
    </div>
  </MainContainer>;

};

export default withRouterWorkaround(withMessages(withRouterWorkaround(withAuthContext(TestPanelResultImageUpload))));
